import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ElasticFormModal, IconButton, InfiniteScroll, ListFilter, ListRow, NoResults, Search, WithSelectable, WithTooltip } from 'basic-components';
import { Exit, FatArrowDown, FatArrowUp } from 'basic-components/icons';
import { AuthEnums } from 'dashboard-services/api/auth';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { Fetching, globalActions, userAuthQueries } from 'primary-components';

import { selectedUserRolesActions } from 'actions/modules/myAccount';
import getApiConfig from 'api/ApiConfig';
import { sendEvent } from 'services/GAService';

import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import FilterCarousel from 'primary/FilterCarousel';
import classNames from 'classnames';
import '../Modal.scss';
import './Modal.scss';

const isUserSelected = (value, user) => {
  let selected = value?.email === user?.email
      selected && Object.entries(value?.roles || {}).forEach(([k, v]) => {
        if(v !== user.roles[k]) {
          selected = false;
        }
      })
  return selected;
}
const SIZE = 20
const EMPTY_ARRAY = [];
const UserPicker = ({ closeModal, defaultValue }) => {
  const dispatch = useDispatch(),
        [value, setValue] = useState(),
        [query, setQuery] = useState(""),
        [sortOrder, setSortOrder] = useState("asc"),
        [roleId, setRoleIds] = useState(),
        getUrl = useCallback(user => DashboardLinkFactory.myAccount.user({ email: user }), []),

        setAscSortingByEmail = useCallback(() => setSortOrder("asc"), []),
        setDescSortingByEmail = useCallback(() => setSortOrder("desc"), []),

        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        [roleQuery, setRoleQuery] = useState(),
        { data: rolesRaw, fetchNextPage: fetchMoreRoles, hasNextPage: hasNextPageRoles, isFetching: isFetchingRoles, isFetchingNextPage: isFetchingNextPageRoles } = userAuthQueries.useSearchRoles({
          getQueryBuilder,
          query: roleQuery,
          shouldBeLimited
        }),
        roles = useMemo(() => rolesRaw?.pages?.flatMap(p => p.items || []).map(item => ({ value: item.egId || item.id, label: item.name })), [rolesRaw?.pages]),
        { data: usersRaw = EMPTY_ARRAY, isPending, fetchNextPage, isFetchingNextPage, hasNextPage } = userAuthQueries.useSearchUsers({
          query,
          size: SIZE,
          roleIds: roleId,
          getQueryBuilder,
          shouldBeLimited,
          sortBy: AuthEnums.ENT_GROUP_SORT_BY.EMAIL,
          sortOrder,
        }),
        users = useMemo(() => (usersRaw?.pages || EMPTY_ARRAY).flatMap(u => u?.items || EMPTY_ARRAY), [usersRaw?.pages]),

        onSubmit = useCallback(() => {
          dispatch(selectedUserRolesActions.addRoles(value?.roles
            .filter(i => i.type !== AuthEnums.ENT_GROUP_TYPE.DEFAULT)
            .map(d => ({ value: d.egId || d.id, label: d.name, tooltip: d.description, type: d.type })), defaultValue)
          )
          dispatch(globalActions.getMessageStore()).success("Roles have been imported.")
        }, [defaultValue, dispatch, value]),

        getUserRegularRoles = useCallback(roles => roles
            .filter(r => r.type === AuthEnums.ENT_GROUP_TYPE.REGULAR)
            .map(r => r.name).join(", "), []),

        submitProps = useMemo(() => ({
          disabled: value === undefined || value === "",
          noDebounce: true,
          type: "button",
          onClick: () => {
            onSubmit()
            closeModal()
          }
        }), [value, closeModal, onSubmit]),
        formProps = useMemo(() => ({
          noDebounce: true
        }), []),
        preventFilterSubmit = useCallback(event => event.preventDefault(), []),
        scrollContRef = useRef()
  
  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "user_picker",
      label: "picker",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        className="ng-common-picker__modal ng-user-picker__modal"
        formProps={formProps}
        onClose={closeModal}
        onSubmit={onSubmit}
        optionalText="Import roles"
        submitProps={submitProps}
        submitText="Select"
        title="Select user"
    >
      <div className="ng-file-picker__modal__top">
        <div className="ng-file-picker__modal__top__search">
          <Search
              onSearch={setQuery}
              size="large"
              variant="outline"
          />
        </div>
        <FilterCarousel
            className="ng-file-picker__modal__filters"
            onClickCapture={preventFilterSubmit}
        >
          <ListFilter
              defaultValue={roleId}
              fetchNextPage={fetchMoreRoles}
              hasNextPage={hasNextPageRoles}
              isFetching={isFetchingRoles || isFetchingNextPageRoles}
              label="Role"
              name="role"
              onChange={setRoleIds}
              onInputChange={setRoleQuery}
              options={roles}
          />
        </FilterCarousel>
      </div>
      <div className="ng-common-picker__modal__headers">
        <div className="ng-common-picker__modal__headers__cell">
          Email
          <div className="ng-common-picker__modal__headers__cell__sort">
            <IconButton 
                className={classNames("ng-common-picker__modal__headers__cell__sort__arrow", { "ng-common-picker__modal__headers__cell__sort__arrow--is-disabled": sortOrder === "asc" })}
                onClick={setAscSortingByEmail}
            >
              <FatArrowUp/>
            </IconButton>
            <IconButton 
                className={classNames("ng-common-picker__modal__headers__cell__sort__arrow", { "ng-common-picker__modal__headers__cell__sort__arrow--is-disabled": sortOrder === "desc" })}
                onClick={setDescSortingByEmail}
            >
              <FatArrowDown/>
            </IconButton>
          </div>
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Company
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Role
        </div>
        <div className="ng-common-picker__modal__headers__cell"/>
      </div>
      <Fetching isFetching={isPending || isFetchingNextPage}/>
      <div 
          className="ng-common-picker__modal__content"
          ref={scrollContRef}
      >
        <InfiniteScroll
            className="ng-common-picker__modal__content__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={fetchNextPage}
            threshold={800}
            useWindow={false}
        >
          {users.map((user, index) => (
            <WithSelectable
                index={index}
                key={`User${index}`}
                selected={isUserSelected(value, user)}
                setSelected={() => setValue(user)}
            >
              <ListRow
                  className="ng-common-picker__modal__content__scroll__row"
                  contentProps={{
                    onDoubleClick: () => {
                      setValue(user)
                      onSubmit()
                      closeModal()
                    }
                  }}
                  onClick={() => setValue(user)}
                  scrollContRef={scrollContRef.current}
              >
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {user.email}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {user.companyName}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {getUserRegularRoles(user.roles)}
                </div>
                <div className="ng-common-picker__modal__exit">
                  <WithTooltip
                      data-place="top"
                      short
                      text="Open pipeline"
                  >
                    <div onClick={() => CommonService.openInNewTab(getUrl(user.email))}>
                      <Exit
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </div>
                  </WithTooltip>
                </div>
              </ListRow>
            </WithSelectable>
          ))}
        </InfiniteScroll>
        <When condition={users.length === 0 && !isPending}>
          {() => (
            <NoResults
                isModal
            />
          )}
        </When>
      </div>
    </ElasticFormModal>
  )
}

UserPicker.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.array.isRequired
}

export default memo(UserPicker);
